import { useState } from 'preact/hooks'
import type { OutboundWebhookSubscriptionViewDto } from '@/types/outboundWebhooks'

export interface OutboundWebhookFormValues {
    Name: string
    Url: string
    Description?: string
    WorkspaceId?: string
    ClearWorkspaceFilter?: boolean
}

interface OutboundWebhookFormProps {
    existing?: OutboundWebhookSubscriptionViewDto
    workspaces: { Id: string; Name: string }[]
    saving: boolean
    error: string | null
    onSubmit: (values: OutboundWebhookFormValues) => void
    onCancel: () => void
}

/**
 * Add or edit a destination. Inline in the detail pane, not a modal — editing a URL is ordinary work
 * and does not warrant taking over the screen.
 */
export function OutboundWebhookForm({
    existing, workspaces, saving, error, onSubmit, onCancel,
}: OutboundWebhookFormProps) {
    const [name, setName] = useState(existing?.Name ?? '')
    const [url, setUrl] = useState(existing?.Url ?? '')
    const [description, setDescription] = useState(existing?.Description ?? '')
    const [workspaceId, setWorkspaceId] = useState(existing?.WorkspaceId ?? '')

    const handleSubmit = (event: Event) => {
        event.preventDefault()
        onSubmit({
            Name: name.trim(),
            Url: url.trim(),
            Description: description.trim() || undefined,
            WorkspaceId: workspaceId || undefined,
            // Explicit, because null and absent are indistinguishable in JSON — without this flag a
            // destination could never be widened back to all workspaces.
            ClearWorkspaceFilter: existing ? workspaceId === '' : undefined,
        })
    }

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-6 max-w-2xl">
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                {existing ? 'Edit destination' : 'Add a destination'}
            </h3>

            <Field label="Name" hint="How this appears in the list. Usually who receives it.">
                <input
                    value={name}
                    onInput={e => setName((e.target as HTMLInputElement).value)}
                    required
                    maxLength={100}
                    placeholder="Omar AI SDR"
                    className={inputClass}
                />
            </Field>

            <Field
                label="Endpoint URL"
                hint="Must be https. Lead details are sent in the request body, so plaintext isn't accepted."
            >
                <input
                    value={url}
                    onInput={e => setUrl((e.target as HTMLInputElement).value)}
                    required
                    type="url"
                    // The server refuses plaintext outright; this only stops the field accepting
                    // what the hint directly above it says is not allowed.
                    pattern="https://.*"
                    title="The endpoint must start with https://"
                    placeholder="https://hooks.example.com/orbitalx/lead"
                    className={`${inputClass} font-mono text-[13px]`}
                />
            </Field>

            <Field label="Workspaces" hint="Which workspaces' leads go to this endpoint.">
                <select
                    value={workspaceId}
                    onChange={e => setWorkspaceId((e.target as HTMLSelectElement).value)}
                    className={inputClass}
                >
                    <option value="">All workspaces</option>
                    {workspaces.map(workspace => (
                        <option key={workspace.Id} value={workspace.Id}>{workspace.Name} only</option>
                    ))}
                </select>
            </Field>

            <Field label="Notes" hint="Optional. What this endpoint is for.">
                <textarea
                    value={description}
                    onInput={e => setDescription((e.target as HTMLTextAreaElement).value)}
                    maxLength={500}
                    rows={2}
                    className={inputClass}
                />
            </Field>

            {error && (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>
            )}

            <div className="flex items-center gap-2 pt-1">
                <button
                    type="submit"
                    disabled={saving || !name.trim() || !url.trim()}
                    className="px-3.5 py-2 text-sm font-semibold rounded-lg bg-primary-500 text-green-950 hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800 transition-all duration-150"
                >
                    {saving ? 'Saving' : existing ? 'Save changes' : 'Add destination'}
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-3.5 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors duration-150"
                >
                    Cancel
                </button>
            </div>

            {!existing && (
                <p className="text-xs text-slate-500 dark:text-slate-400 border-l border-slate-300 dark:border-slate-600 pl-3">
                    You'll get a signing secret once the destination is created. It's shown a single
                    time, so have somewhere to put it before you save.
                </p>
            )}
        </form>
    )
}

const inputClass =
    'w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors duration-150'

function Field({ label, hint, children }: { label: string; hint?: string; children: preact.ComponentChildren }) {
    return (
        <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold tracking-wider uppercase text-slate-500 dark:text-slate-400">
                {label}
            </span>
            {children}
            {hint && <span className="text-xs text-slate-500 dark:text-slate-400">{hint}</span>}
        </label>
    )
}
